/**
 * 财富健康 W · 健康资本化资产工具
 *
 * 产品目标：把抽象的健康信托讲清楚 —— 我买了什么、覆盖什么、什么时候触发、
 * 触发之后发生什么。
 *
 * 与原型的差别：
 *   - 等级、评分、方案、配置比例、保护预案、时间轴全部来自 GET /api/wealth/overview；
 *   - 当前信托方案的真值源是接口返回的 plans[].current（落库在 wealth_profiles），
 *     不是本地 state，切屏与刷新都不会丢；
 *   - 原型写死的「4 项生效」改为按 statusLabel 实时统计（实际为 2 项已生效 + 2 项已建立）；
 *   - 时间轴按接口的 sortKey 排序渲染，规划中的阶段弱化并标注「规划中」。
 */
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ProtectionPlan, TrustPlanKey, WealthOverview } from '@hew3/shared';
import { resolveColor } from '@hew3/shared';
import { ApiError, api } from '../../lib/api.ts';
import { charts } from '../../lib/charts.ts';
import { formatDate } from '../../lib/format.ts';
import { useUi } from '../../store/ui.ts';
import { Badge, Card, Chart, ListItem, StateBox, Timeline } from '../../components/ui.tsx';

/**
 * 50+ 用户可读性：设计系统的 .badge 默认 10.5px 偏小，
 * 这里统一放大到 11.5px（仍然复用 Badge 组件与 .badge 类，不改动设计系统本身）。
 */
function ReadableBadge({
  tone,
  children,
}: {
  tone: 'h' | 'e' | 'w' | 'ok' | 'warn' | 'danger' | 'ghost';
  children: ReactNode;
}) {
  return (
    <Badge tone={tone}>
      <span style={{ fontSize: 11.5 }}>{children}</span>
    </Badge>
  );
}

/** 保护预案状态 → 徽章色调（文案一律用接口返回的 statusLabel）。 */
function protectionTone(status: ProtectionPlan['status']): 'ok' | 'h' | 'ghost' {
  if (status === 'active') return 'ok';
  if (status === 'established') return 'h';
  return 'ghost';
}

/** 按接口返回的 statusLabel 分组计数，避免把数量写死在界面上。 */
function summarizeProtections(items: ProtectionPlan[]): { label: string; count: number }[] {
  const groups: { label: string; count: number }[] = [];
  for (const item of items) {
    const found = groups.find((group) => group.label === item.statusLabel);
    if (found) found.count += 1;
    else groups.push({ label: item.statusLabel, count: 1 });
  }
  return groups;
}

export function WealthPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showToast = useUi((s) => s.showToast);

  const overviewQuery = useQuery({
    queryKey: ['wealth'],
    queryFn: () => api.get<WealthOverview>('/api/wealth/overview'),
  });

  const switchPlan = useMutation({
    mutationFn: (planKey: TrustPlanKey) => api.put<WealthOverview>('/api/wealth/plan', { planKey }),
    onSuccess: (updated) => {
      // 接口直接返回重查后的完整总览，先写缓存再统一失效，保证真值源仍在服务端
      queryClient.setQueryData<WealthOverview>(['wealth'], updated);
      void queryClient.invalidateQueries({ queryKey: ['wealth'] });
      showToast(`已切换为「${updated.trustPlanName}」`, 'ok');
    },
    onError: () => showToast('信托方案切换失败，请稍后重试', 'error'),
  });

  const head = (
    <div className="scr-head">
      <button type="button" className="back" aria-label="返回" onClick={() => navigate(-1)}>
        <svg viewBox="0 0 24 24">
          <path d="M15 5l-7 7 7 7" />
        </svg>
      </button>
      <div className="scr-title">
        财富健康 W<small>健康资本化资产工具</small>
      </div>
    </div>
  );

  if (overviewQuery.isLoading) {
    return (
      <div className="page">
        {head}
        <div className="skeleton skel-card" style={{ height: 168 }} />
        <div className="skeleton skel-card" style={{ height: 150 }} />
        <div className="skeleton skel-card" style={{ height: 180 }} />
        <div className="skeleton skel-card" style={{ height: 240 }} />
      </div>
    );
  }

  if (overviewQuery.isError || !overviewQuery.data) {
    const reason = overviewQuery.error instanceof ApiError ? overviewQuery.error.message : '';
    return (
      <div className="page">
        {head}
        <StateBox
          kind="error"
          title="财富健康数据加载失败"
          desc={reason || '请检查网络后重试；若持续失败，可联系专属健康管家核对建档状态。'}
          action={
            <button type="button" className="btn sm" onClick={() => void overviewQuery.refetch()}>
              重新加载
            </button>
          }
        />
      </div>
    );
  }

  const data = overviewQuery.data;
  const { plans, allocation, protections, timeline } = data;

  if (plans.length === 0 && allocation.length === 0 && protections.length === 0 && timeline.length === 0) {
    return (
      <div className="page">
        {head}
        <StateBox
          title="暂无财富健康档案"
          desc="接口已连通，但当前成员还没有可展示的信托方案、配置比例、保护预案与传承路径，请联系专属健康管家完成建档。"
        />
      </div>
    );
  }

  const statusGroups = summarizeProtections(protections);
  const statusSummary = statusGroups.map((group) => `${group.count} 项${group.label}`).join(' · ');

  return (
    <div className="page">
      {head}

      {/* ---------------- 财富健康等级卡 ---------------- */}
      <div className="member-card">
        <div className="row between" style={{ position: 'relative', alignItems: 'flex-start' }}>
          <div className="grow">
            <div className="mc-lv">WEALTH SAFETY · 财富健康等级</div>
            <div className="mc-name">
              {data.grade} 级 · {data.gradeText}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="mc-k" style={{ fontSize: 11 }}>
              W-SCORE
            </div>
            <div className="num" style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1, color: resolveColor('w') }}>
              {data.score}
            </div>
          </div>
        </div>

        <div className="mc-row">
          <div className="grow">
            <div className="mc-k" style={{ fontSize: 11 }}>
              当前信托方案
            </div>
            <div className="mc-v">{data.trustPlanName}</div>
          </div>
          <div className="grow">
            <div className="mc-k" style={{ fontSize: 11 }}>
              信托设立时间
            </div>
            <div className="mc-v">{data.trustSince}</div>
          </div>
        </div>

        <div className="mc-row">
          <div className="grow">
            <div className="mc-k" style={{ fontSize: 11 }}>
              健康信托作用
            </div>
            <div style={{ fontSize: 12.5, marginTop: 3, lineHeight: 1.6 }}>
              可降低传承期健康风险损失 {data.lossAvoidText}
            </div>
          </div>
        </div>

        <div style={{ position: 'relative', fontSize: 11, color: 'var(--muted-2)', marginTop: 12 }}>
          评分数据截至 {formatDate(data.measuredAt)}
        </div>
      </div>

      {/* ---------------- 健康信托三档 ---------------- */}
      <Card title="健康信托方案" more={<span>{plans.length} 档可选</span>}>
        {plans.length === 0 ? (
          <StateBox title="暂无可用方案" desc="方案目录尚未同步，请稍后重试或联系专属健康管家。" />
        ) : (
          <>
            <div className="trust-grid">
              {plans.map((plan) => {
                const isCurrent = plan.current;
                const isBusy = switchPlan.isPending && switchPlan.variables === plan.key;
                return (
                  <button
                    key={plan.key}
                    type="button"
                    className={`trust${isCurrent ? ' on' : ''}`}
                    aria-pressed={isCurrent}
                    disabled={isCurrent || switchPlan.isPending}
                    onClick={() => switchPlan.mutate(plan.key)}
                    style={{
                      font: 'inherit',
                      color: 'inherit',
                      opacity: isBusy ? 0.6 : 1,
                      cursor: isCurrent || switchPlan.isPending ? 'default' : 'pointer',
                    }}
                  >
                    <div className="tr-n" style={{ fontSize: 13 }}>
                      {plan.name}
                    </div>
                    <div className="tr-p" style={{ fontSize: 11.5 }}>
                      {plan.priceFrom}
                    </div>
                    <div className="tr-s" style={{ fontSize: 11 }}>
                      {plan.summary}
                    </div>
                    <div style={{ marginTop: 8 }}>
                      {isBusy ? (
                        <span style={{ fontSize: 11, color: 'var(--h)' }}>切换中…</span>
                      ) : isCurrent ? (
                        <ReadableBadge tone="w">当前方案</ReadableBadge>
                      ) : (
                        <span style={{ fontSize: 11, color: 'var(--muted-2)' }}>点击切换</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="note-src" style={{ marginTop: 12 }}>
              <b>切换说明</b>　方案变更即时写入你的成员档案并留痕，当前方案以服务端返回为准，切换后刷新不会丢失。
            </div>
          </>
        )}
      </Card>

      {/* ---------------- 资产配置全景 ---------------- */}
      <Card title="资产配置全景">
        {allocation.length === 0 ? (
          <StateBox title="暂无配置数据" desc="尚未生成资产配置全景，请联系专属健康管家完成资产尽调。" />
        ) : (
          <>
            <div className="row" style={{ gap: 14, alignItems: 'center' }}>
              <Chart
                html={charts.donut({
                  segments: allocation.map((slice) => ({ value: slice.value, color: slice.color })),
                  size: 116,
                  thickness: 15,
                })}
              />
              <div className="grow">
                {allocation.map((slice, index) => (
                  <div className="row between" key={`${slice.label}-${index}`} style={{ padding: '5px 0' }}>
                    <span className="row-flex" style={{ gap: 7 }}>
                      <i
                        style={{
                          display: 'inline-block',
                          width: 9,
                          height: 9,
                          borderRadius: 3,
                          background: resolveColor(slice.color),
                        }}
                      />
                      <span style={{ fontSize: 12 }}>{slice.label}</span>
                    </span>
                    <span className="num" style={{ fontSize: 12.5, fontWeight: 600 }}>
                      {slice.value}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="note-src" style={{ marginTop: 12 }}>
              <b>配置口径</b>　配置比例基于成员授权范围内的资产结构估算，用于观察健康风险与资产结构的匹配度，
              不构成投资建议。
            </div>
          </>
        )}
      </Card>

      {/* ---------------- 健康风险保护预案 ---------------- */}
      <Card
        title="健康风险保护预案"
        more={statusSummary ? <span>{statusSummary}</span> : undefined}
      >
        {protections.length === 0 ? (
          <StateBox title="暂无保护预案" desc="尚未建立健康风险保护预案，可联系专属健康管家安排设立。" />
        ) : (
          <>
            {protections.map((item) => (
              <ListItem
                key={item.id}
                icon={<span style={{ fontSize: 18 }}>{item.icon}</span>}
                title={item.name}
                sub={<span style={{ fontSize: 11.5 }}>{item.desc}</span>}
                right={<ReadableBadge tone={protectionTone(item.status)}>{item.statusLabel}</ReadableBadge>}
              />
            ))}
            <div className="note-src" style={{ marginTop: 10 }}>
              <b>预案口径</b>　保护预案定义触发条件与响应动作；触发后的资金拨付与法律服务由持牌合作机构执行，
              平台负责预案匹配与过程留痕。
            </div>
          </>
        )}
      </Card>

      {/* ---------------- 传承路径时间轴 ---------------- */}
      <Card title="传承路径时间轴" more={<span>{timeline.length} 个阶段</span>}>
        {timeline.length === 0 ? (
          <StateBox title="暂无时间轴记录" desc="传承路径尚未生成，请联系专属健康管家安排启动评估。" />
        ) : (
          <>
            <Timeline
              items={timeline.map((item) => ({
                id: item.id,
                date: (
                  <span style={{ fontSize: 11.5 }}>
                    {item.dateLabel}
                    {item.planned && (
                      <span style={{ marginLeft: 6 }}>
                        <ReadableBadge tone="ghost">规划中</ReadableBadge>
                      </span>
                    )}
                  </span>
                ),
                title: item.planned ? <span style={{ opacity: 0.7 }}>{item.title}</span> : item.title,
                desc: item.planned ? <span style={{ opacity: 0.7 }}>{item.desc}</span> : item.desc,
                isCurrent: item.isCurrent,
              }))}
            />
            <div className="sec-note" style={{ fontSize: 11 }}>
              阶段按接口返回的排序键排列；标注「规划中」的阶段为预案，实际启动时间以评估结果为准。
            </div>
          </>
        )}
      </Card>

      <div className="note-src" style={{ marginTop: 12 }}>
        <b>业务边界</b>　金融、信托、法务业务由具备相应牌照的合作机构落地实施，本平台不直接提供金融产品；
        页面中的评分与预案为健康资本化管理工具的输出，不构成投资建议，具体方案须由持牌机构与人类专家结合
        完整尽调后确认。
      </div>
    </div>
  );
}
